/**
 * @description This will remove any zeros at the beginning of a number string
 * @example trimLeadingZeros("004") //returns "4"
 * @example trimLeadingZeros("0400") //return "400"
 */
export function trimLeadingZeros(str: string | number): string {
  return str.toString().replace(/^0+/, '');
}

/**
 * @description This converts a string representation of a number into a real number, which also trims off any excess zeros from the end
 * @example trimFollowingZeroes("20.000") //returns "20"
 * @example trimFollowingZeroes(45.04) //return "45.04" (since there are no extra zeros to trim)
 * @example trimFollowingZeroes("45.040") //returns "45.04"
 */
export function trimDecimalZeros(str: string | number): string {
  return Number(str).toString();
}

/**
 * @description Returns an SAP number as a string. Any leading zeros are removed, and any trailing zeros in a decimal are removed
 * @example standardizeSapNumber("000400.00") // "400"
 */
function standardizeSapNumber(value: string | number): string {
  const num = trimDecimalZeros(trimLeadingZeros(value));
  if (num === 'NaN') {
    console.error(`Error when converting "${value.toString()}" to a standardized SAP number! The value is "NaN"!`);
  }
  return num;
}

/**
 * @description Transforms a string so that every word has the first letter capitalized
 * @deprecated This is not needed at all. Use the TailwindCSS class `capitalize` in the display instead!
 * @see https://tailwindcss.com/docs/text-transform#transforming-text
 */
export function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * @description Transforms a string of space separated words into a camelCase string.
 * @example camelize('Hello my name is bob') //'helloMyNameIsBob'
 */
export function camelize(str: string): string {
  return str
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (word: string, index: number) => {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, '');
}

export function camelCaseToWords(str: string): string {
  const camelCaseWords = str.replace(/([A-Z])/g, ' $1');
  return camelCaseWords.charAt(0).toUpperCase() + camelCaseWords.slice(1);
}

/**
 * @description Performs an addition operation on the two passed in string representations of numbers. Always returns a number with two decimal places as a string
 * @example addDecimals("1.5", "2.6") // "4.10"
 * @example addDecimals("1.50000", "2.60000") // "4.10"
 * @todo This is potentially inaccurate since JavaScript uses floating-point math which is sometimes very strange!
 */
export function addDecimals(num1: number | string, num2: number | string): string {
  return trimDecimalZeros((Number(num1) + Number(num2)).toFixed(2));
}

/**
 * @description Returns the first character from the passed in string. You might not need to use this in some cases, use `'hello'.startsWith('h')` if you are checking a string
 * @example getFirstChar("hello") // "h"
 * @deprecated this is still used in ECC, but in S4, the activity service will handle this.
 */
export function getFirstChar(str: string): string {
  return str.substring(0, 1);
}

/**
 * @description Returns the passed in string, but with the first character removed
 * @example removeFirstChar("hello") //"ello" (it becomes British in this example)
 * @deprecated this is still used in ECC, but in S4, the activity service will handle this.
 */
export function removeFirstChar(str: string): string {
  return str.substring(1);
}

/**
 * @description Gets the first letters from the first word and last word in a string
 * @example this.getFirstLetters('hello world') //"hw"
 * @example this.getFirstLetters('hello world i am bill') //"hb"
 */
export function getFirstLetters(str: string): string {
  if (str === '') return '';
  const firstLetters = str.split(' ').map((word) => word[0]);
  return `${firstLetters[0] ?? ''}${firstLetters[firstLetters.length - 1] ?? ''}`;
}

/**
 * @description Compares two SAP numbers and reports if they are the same or not.
 * Numbers with or without leading zeros and extra decimal zeros can be properly compared
 * @example safeCompare("000425", "425.0000") // true
 * @example safeCompare("0004250", "425.0000") // false
 * @example safeCompare("425", "425.0000") // true
 * @example safeCompare("4250", "425.0000") // false
 */
export function safeCompare(value1: string | number | undefined, value2: string | number | undefined): boolean {
  const safeValue1 = value1 ?? '';
  const safeValue2 = value2 ?? '';
  return standardizeSapNumber(safeValue1) === standardizeSapNumber(safeValue2);
}

/**
 * @description Forces a number to have the provided number decimal places in all cases, even for whole numbers. Default to 2 decimal places
 * @example limitDecimalDisplay(2.1) // 2.10
 * @example limitDecimalDisplay(2) // 2.00
 * @example limitDecimalDisplay(3.14159) // 3.14
 */
export function limitDecimalDisplay(num: string | number, places = 2): string {
  return Number(num).toFixed(places);
}

/**
 * @description Takes a number and converts it to a string formatted as an English/US number string with commas and a two digit decimal place.
 * @param [shouldFormatAsCurrency] Set this to `true` to also add a USD $ currency symbol to the beginning
 * @example toCurrencyString(2) // "2.00"
 * @example toCurrencyString(2, true) // "$2.00"
 * @example toCurrencyString(19.989999) // "19.99"
 * @example toCurrencyString(12345, true) //"$12,345.00"
 */
export function toCurrencyString(numberString: number | string, shouldFormatAsCurrency = false): string {
  const num = Number(numberString);
  if (shouldFormatAsCurrency) return new Intl.NumberFormat('en-us', { style: 'currency', currency: 'USD' }).format(num);
  return new Intl.NumberFormat('en-us', { minimumFractionDigits: 2 }).format(num);
}

/**@description Converts an sap "Psuedo" array and then returns the value at the specified index. Returns "" in out of bounds case. Psuedo arrays look like "0,1,2,3,4,5"
 * @example parsePsuedoArray("0,1,2,3" 1) => "1"
 * @example parsePsuedoArray("0,1,2,3" -1) => ""
 * @example parsePsuedoArray("0,1,2,3" 5) => ""
 */

export function parsePseudoArray(stringArray: string, index: number): string {
  return stringArray.split(',')[index] ?? '';
}
